/**
 * Your context: what eve should know about the person, not the firm — role,
 * department, how they work and how they want answers, plus standing
 * instructions, the automatic recall preference and whether Amoreg learns how
 * they work. Text is carried into each turn beside the organisation's
 * context; the preferences gate retrieval and learning. What has been learned
 * in the active organisation is listed below the form (LearnedNotesSection).
 *
 * @module components/settings/YourContextSettings
 */
import { useEffect, useState } from "react";

import {
  EMPTY_USER_CONTEXT,
  fetchUserContext,
  saveUserContext,
  useAccountProfile,
  type UserContext,
} from "../../state/accountProfile";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { LearnedNotesSection } from "./LearnedNotesSection";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";

const LIMITS = { jobTitle: 160, department: 160, personalContext: 4_000, workingStyle: 2_000, responsePreferences: 2_000 };

function pinnedToText(pinned: ReadonlyArray<string>): string {
  return pinned.join("\n");
}

function textToPinned(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, 20);
}

export function YourContextSettingsPanel() {
  const { account, isPending } = useAccountProfile();
  const [saved, setSaved] = useState<UserContext | null>(null);
  const [draft, setDraft] = useState<UserContext>(EMPTY_USER_CONTEXT);
  const [pinnedText, setPinnedText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (!account?.subject.startsWith("workos:")) return;
    let cancelled = false;
    setSaved(null);
    setError(null);
    setSavedAt(null);
    void fetchUserContext().then((context) => {
      if (cancelled) return;
      if (context === null) {
        setError("Your context could not be loaded. Try again before changing your settings.");
        return;
      }
      setSaved(context);
      setDraft(context);
      setPinnedText(pinnedToText(context.pinnedInstructions));
    });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt, account?.subject]);

  const dirty =
    saved !== null &&
    (draft.automaticRecall !== saved.automaticRecall ||
      draft.learnFromConversations !== saved.learnFromConversations ||
      draft.jobTitle !== saved.jobTitle ||
      draft.department !== saved.department ||
      draft.personalContext !== saved.personalContext ||
      draft.workingStyle !== saved.workingStyle ||
      draft.responsePreferences !== saved.responsePreferences ||
      pinnedToText(textToPinned(pinnedText)) !== pinnedToText(saved.pinnedInstructions));

  const set = (
    key: Exclude<keyof UserContext, "automaticRecall" | "learnFromConversations" | "pinnedInstructions">,
    value: string,
  ) =>
    setDraft((current) => ({ ...current, [key]: value.length === 0 ? null : value }));

  const save = async () => {
    setIsSaving(true);
    setError(null);
    const next: UserContext = { ...draft, pinnedInstructions: textToPinned(pinnedText) };
    const failure = await saveUserContext(next);
    setIsSaving(false);
    if (failure) {
      setError(failure);
      return;
    }
    setSaved(next);
    setSavedAt(Date.now());
  };

  if (isPending && account === null) {
    return <SettingsPageContainer>{null}</SettingsPageContainer>;
  }
  if (account === null || !account.subject.startsWith("workos:")) {
    return (
      <SettingsPageContainer>
        <SettingsSection id="your-context" title="Your context">
          <SettingsRow
            title="Not available for this session"
            description="Your context lives on your Amoreg account. Sign in with your Amoreg email to edit it."
          />
        </SettingsSection>
      </SettingsPageContainer>
    );
  }

  return (
    <SettingsPageContainer>
      <SettingsSection
        id="your-context"
        title="Your context"
        headerAction={
          <div className="flex items-center gap-2">
            {savedAt !== null && !dirty ? (
              <span className="text-xs text-muted-foreground">Saved</span>
            ) : null}
            <Button type="button" size="sm" disabled={!dirty || isSaving || saved === null} onClick={() => void save()}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      >
        <SettingsRow
          id="your-context-recall"
          title="Use past conversations automatically"
          description="Bring relevant excerpts from your earlier conversations in this organisation into new replies. You can still ask Amoreg to search your history when this is off."
          control={
            <Switch
              aria-label="Use past conversations automatically"
              checked={draft.automaticRecall}
              disabled={saved === null || isSaving}
              onCheckedChange={(automaticRecall) =>
                setDraft((current) => ({ ...current, automaticRecall }))
              }
            />
          }
        />
        <SettingsRow
          id="your-context-learning"
          title="Learn from my conversations"
          description="Amoreg notices the habits you repeat — the format you ask for, the order you work in, the sources you check first — and applies them in new conversations, so you don't have to ask each time. Used only to improve how Amoreg works for you, kept per organisation, and listed below."
          control={
            <Switch
              aria-label="Learn from my conversations"
              checked={draft.learnFromConversations}
              disabled={saved === null || isSaving}
              onCheckedChange={(learnFromConversations) =>
                setDraft((current) => ({ ...current, learnFromConversations }))
              }
            />
          }
        />
        <SettingsRow
          id="your-context-role"
          title="Role"
          description="Your job title and department. Amoreg uses them to pitch answers at the right level — an MLRO gets the rule, a director gets the decision."
          control={
            <div className="flex w-full flex-col gap-2 sm:w-72">
              <Input
                aria-label="Job title"
                placeholder="Job title, e.g. MLRO"
                value={draft.jobTitle ?? ""}
                maxLength={LIMITS.jobTitle}
                disabled={saved === null}
                onChange={(event) => set("jobTitle", event.currentTarget.value)}
              />
              <Input
                aria-label="Department"
                placeholder="Department, e.g. Compliance"
                value={draft.department ?? ""}
                maxLength={LIMITS.department}
                disabled={saved === null}
                onChange={(event) => set("department", event.currentTarget.value)}
              />
            </div>
          }
        />
        <SettingsRow
          id="your-context-about"
          title="About you"
          description="What you are responsible for, which entities or jurisdictions you cover, what you are working on. Written into every conversation, beside the company context."
        >
          <Textarea
            aria-label="About you"
            placeholder="I look after AML for the Guernsey and Jersey entities, report to the board quarterly, and am preparing for the GFSC visit in November…"
            rows={6}
            value={draft.personalContext ?? ""}
            maxLength={LIMITS.personalContext}
            disabled={saved === null}
            className="mt-2 font-normal"
            onChange={(event) => set("personalContext", event.currentTarget.value)}
          />
        </SettingsRow>
        <SettingsRow
          id="your-context-style"
          title="How you work"
          description="Anything about your working style Amoreg should respect — the systems you use, how you review, what you already know."
        >
          <Textarea
            aria-label="How you work"
            placeholder="I review in Word with tracked changes; assume I know the Handbook, cite rule numbers…"
            rows={3}
            value={draft.workingStyle ?? ""}
            maxLength={LIMITS.workingStyle}
            disabled={saved === null}
            className="mt-2 font-normal"
            onChange={(event) => set("workingStyle", event.currentTarget.value)}
          />
        </SettingsRow>
        <SettingsRow
          id="your-context-answers"
          title="How you want answers"
          description="Length, tone, format. Short and decisive, or thorough with the reasoning shown."
        >
          <Textarea
            aria-label="How you want answers"
            placeholder="Lead with the conclusion, then the rule, then the caveats. British English. Tables over prose for comparisons."
            rows={3}
            value={draft.responsePreferences ?? ""}
            maxLength={LIMITS.responsePreferences}
            disabled={saved === null}
            className="mt-2 font-normal"
            onChange={(event) => set("responsePreferences", event.currentTarget.value)}
          />
        </SettingsRow>
        <SettingsRow
          id="your-context-instructions"
          title="Standing instructions"
          description="One per line. Rules Amoreg follows in every conversation with you."
        >
          <Textarea
            aria-label="Standing instructions"
            placeholder={"Always name the regulator and the rule.\nNever draft board minutes without asking first."}
            rows={4}
            value={pinnedText}
            disabled={saved === null}
            className="mt-2 font-normal"
            onChange={(event) => setPinnedText(event.currentTarget.value)}
          />
        </SettingsRow>
        {error ? (
          <p role="alert" className="px-3 pb-2 text-sm text-destructive sm:px-4">
            {error}
            {saved === null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="ml-2"
                onClick={() => setLoadAttempt((attempt) => attempt + 1)}
              >
                Try again
              </Button>
            ) : null}
          </p>
        ) : null}
      </SettingsSection>
      <LearnedNotesSection />
    </SettingsPageContainer>
  );
}
